import { useCallback, useEffect, useState } from "react";
import { FEATURES, LIMIT_KEYS, type LimitKey, type MyPackageDto, type MySubscriptionDto, type PackageTerms, type PublicPackageDto } from "@delta/shared";
import { useLang, type T } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/Toast";
import { PageHead, Shell } from "../../components/Shell";

// Settings › Package (migration 043): the business's package, its dates and days left, what it includes, what the
// business uses against its limits, scheduled changes, the published packages side by side, and a request to Delta
// for a renewal or a change. A request is reviewed by hand at Delta: nothing is charged here and no payment is taken.

const LIMIT_LABEL: Record<LimitKey, string> = { users: "users", branches: "branches", warehouses: "warehouses", treasuries: "treasuries", posDevices: "pkgPosDevices" };

export const priceText = (t: T, p: Pick<PackageTerms, "kind" | "price" | "currency" | "billingPeriod" | "trialDays">) =>
  p.kind === "trial" ? t("pkgTrialDays", { n: p.trialDays ?? 0 })
    : p.price ? `${p.price} ${p.currency ?? ""}${p.billingPeriod ? ` / ${t(`pkgPeriod_${p.billingPeriod}`)}` : ""}` : t("pkgPriceNotSet");

/** Packages side by side: price, every module and every limit. Used by the owner's screen and the platform portal. */
export function Comparison({ rows, current, testId }: { rows: { id: string; nameEn: string; nameAr: string; terms: PackageTerms }[]; current?: string | null; testId: string }) {
  const { t, L } = useLang();
  if (!rows.length) return <div className="state-box"><p>{t("pkgNoPublic")}</p></div>;
  const yes = <span className="pill good"><Icon name="check" />{t("pkgIncluded")}</span>;
  const no = <span className="pill">{t("pkgNotIncluded")}</span>;
  return (
    <div className="pkg-scroll"><table className="tbl pkg-cmp" data-testid={testId}>
      <thead><tr><th />{rows.map((r) => <th key={r.id} className={r.id === current ? "on" : ""}>{L(r)}{r.id === current && <div className="pill info">{t("pkgYours")}</div>}</th>)}</tr></thead>
      <tbody>
        <tr><th>{t("pkgKind")}</th>{rows.map((r) => <td key={r.id}>{t(`pkgKind_${r.terms.kind}`)}</td>)}</tr>
        <tr><th>{t("pkgPrice")}</th>{rows.map((r) => <td key={r.id} className="num">{priceText(t, r.terms)}</td>)}</tr>
        <tr className="sep"><th colSpan={rows.length + 1}>{t("pkgModules")}</th></tr>
        {FEATURES.map((f) => <tr key={f} data-feature={f}><th>{t(`pkgFeature_${f}`)}</th>{rows.map((r) => <td key={r.id}>{r.terms.features.includes(f) ? yes : no}</td>)}</tr>)}
        <tr className="sep"><th colSpan={rows.length + 1}>{t("pkgLimits")}</th></tr>
        {LIMIT_KEYS.map((k) => <tr key={k}><th>{t(LIMIT_LABEL[k])}</th>{rows.map((r) => <td key={r.id} className="num">{r.terms.limits[k]}</td>)}</tr>)}
      </tbody>
    </table></div>
  );
}

const termsOfPublic = (p: PublicPackageDto): PackageTerms => ({ kind: p.kind, trialDays: p.trialDays, billingPeriod: p.billingPeriod, currency: p.currency, price: p.price, features: p.features, limits: p.limits });

export function PackageSettings() {
  const { t, L, lang } = useLang();
  const { refresh } = useSession();
  const toast = useToast();
  const [d, setD] = useState<MyPackageDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [form, setForm] = useState<{ kind: "renewal" | "change"; planId: string; note: string; requestId: string }>({ kind: "renewal", planId: "", note: "", requestId: crypto.randomUUID() });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api<MyPackageDto | null>("GET", "/package").then((x) => (x ? setD(x) : setFailed(t("pkgNotOnPackage"))), (e) => setFailed(errorText(t, e))), [t]);
  useEffect(() => { void load(); }, [load]);
  const send = async () => {
    setBusy(true);
    try {
      setD(await api<MyPackageDto>("POST", "/package/requests", { requestId: form.requestId, kind: form.kind, planId: form.kind === "change" ? form.planId || null : null, note: form.note }));
      setForm({ kind: "renewal", planId: "", note: "", requestId: crypto.randomUUID() });
      toast(t("pkgRequestSent"));
    } catch (e) { toast(errorText(t, e)); } finally { setBusy(false); }
  };
  const withdraw = async (id: string) => {
    try { setD(await api<MyPackageDto>("POST", `/package/requests/${id}/withdraw`)); toast(t("pkgRequestWithdrawn")); } catch (e) { toast(errorText(t, e)); }
  };
  useEffect(() => { void refresh(); }, [d?.state, refresh]);
  const head = <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("setPackage")]]} title={t("setPackage")} />;
  if (failed) return <Shell navKey="/settings/package">{head}<section className="card"><div className="state-box error"><Icon name="alert" /><h3>{failed}</h3></div></section></Shell>;
  if (!d) return <Shell navKey="/settings/package">{head}<section className="card"><div className="state-box"><p>{t("loading")}</p></div></section></Shell>;
  const c = d.current;
  const lim = d.effective.limits;
  const meter = (k: LimitKey) => {
    const a = d.usage[k], b = lim?.[k] ?? 0;
    return (
      <div key={k} data-limit={k}><div className="pn-k">{t(LIMIT_LABEL[k])}</div>
        <div className={`meter ${a > b ? "bad" : b === 0 || a / b >= 0.8 ? "warn" : ""}`}><i style={{ width: `${b ? Math.min(100, (a / b) * 100) : a ? 100 : 0}%` }} /></div>
        <div className="hint num">{t("pkgUsedOf", { a, b })}{a > b && <> · <b className="bad-text">{t("pkgOverLimit")}</b></>}</div></div>
    );
  };
  const statePill = <span className={`pill ${d.state === "paid" ? "good" : d.state === "trial" ? "info" : "bad"}`} data-testid="pkg-state" data-state={d.state}>{t(`pkgState_${d.state}`)}</span>;
  const subRow = (s: MySubscriptionDto) => (
    <tr key={s.id} data-sub={s.id}>
      <td>{L(s.plan)} <span className="pf-small">v{s.version}</span></td><td>{t(`pkgKind_${s.kind}`)}</td>
      <td className="num">{s.startsOn} → {s.endsOn}{s.originalEndsOn && <div className="pf-small">{t("pkgExtendedFrom", { d: s.originalEndsOn })}</div>}</td>
      <td>{t(`pkgChange_${s.changeKind}`)}</td>
      <td>{s.payment === "recorded_manual" ? <span className="pill">{t("pkgPaymentManual")}</span> : <span className="pf-small">{t("pkgPaymentNone")}</span>}</td>
      <td>{s.status === "cancelled" ? <span className="pill">{t("pkgCancelled")}</span> : s.startsOn > d.today ? <span className="pill info">{t("pkgScheduled")}</span> : s.endsOn < d.today ? <span className="pill">{t("pkgEnded")}</span> : <span className="pill good">{t("pkgCurrent")}</span>}</td>
    </tr>
  );
  const cmp = d.packages.map((p) => ({ id: p.id, nameEn: p.nameEn, nameAr: p.nameAr, terms: termsOfPublic(p) }));
  return (
    <Shell navKey="/settings/package">
      {head}
      <section className="card" data-testid="pkg-current">
        <div className="card-head"><h2>{t("pkgYourPackage")}</h2>{statePill}</div>
        <div className="card-body pkg-now">
          <div className="pkg-main">
            <div className="pn-k">{t("pkgName")}</div>
            <div className="pkg-name" data-testid="pkg-name">{c ? L(c.plan) : t(d.state === "expired" ? "pkgNoneCurrentExpired" : "pkgNoneCurrent")}</div>
            {c && (lang === "ar" ? c.plan.descriptionAr : c.plan.descriptionEn) && <p className="pf-small">{lang === "ar" ? c.plan.descriptionAr : c.plan.descriptionEn}</p>}
            {c && <dl className="pkg-kv">
              <dt>{t("pkgStarts")}</dt><dd className="num">{c.startsOn}</dd>
              <dt>{t("pkgEnds")}</dt><dd className="num" data-testid="pkg-ends">{c.endsOn}{c.originalEndsOn && <div className="pf-small">{t("pkgExtendedFrom", { d: c.originalEndsOn })}</div>}</dd>
              <dt>{t("pkgDaysLeft")}</dt><dd className="num" data-testid="pkg-days-left">{t("pkgDaysN", { n: d.daysLeft ?? 0 })}</dd>
              <dt>{t("pkgPrice")}</dt><dd className="num">{priceText(t, c.terms)}</dd>
              <dt>{t("pkgPayment")}</dt><dd>{c.payment === "recorded_manual" ? t("pkgPaymentManualLong") : t(c.kind === "trial" ? "pkgPaymentTrial" : "pkgPaymentNoneLong")}</dd>
            </dl>}
          </div>
          {/* Closed (migration 044): no module is open, so none is listed as included. */}
          {d.access === "open" && <div className="pkg-features" data-testid="pkg-features">
            <div className="pn-k">{t("pkgModules")}</div>
            <ul>
              {FEATURES.map((f) => { const on = d.effective.features.includes(f); return <li key={f} className={on ? "on" : "off"} data-feature={f} data-on={on}><Icon name={on ? "check" : "lock"} /><span>{t(`pkgFeature_${f}`)}{!on && <span className="pf-small"> · {t("pkgNotIncluded")}</span>}</span></li>; })}
            </ul>
          </div>}
        </div>
        {d.expiringSoon && c && <div className="pkg-notice warn" role="status" data-testid="pkg-expiring"><Icon name="alert" /><span>{t(c.kind === "trial" ? "pkgNoticeTrial" : "pkgNoticePaid", { d: c.endsOn, n: d.daysLeft ?? 0 })} {t(d.rules === "enforced" ? "pkgAfterEndClosed" : "pkgAfterEnd")}</span></div>}
        {!c && <div className={`pkg-notice ${d.access !== "open" || d.state === "expired" ? "bad" : "warn"}`} role="status" data-testid="pkg-no-current" data-access={d.access}><Icon name="alert" />
          <span>{t(d.access === "expired" ? "pkgClosedEndedBody" : d.access === "none" ? "pkgClosedNoneBody" : d.state === "expired" ? "pkgExpiredBody" : "pkgNoneBody")}</span></div>}
      </section>

      <section className="card" data-testid="pkg-usage">
        <div className="card-head"><h2>{t("pkgUsage")}</h2></div>
        <div className="card-body"><div className="pkg-meters">{LIMIT_KEYS.map(meter)}</div><p className="pf-small">{t("pkgUsageHint")}</p></div>
      </section>

      {d.scheduled.length > 0 && (
        <section className="card list-card" data-testid="pkg-scheduled">
          <div className="card-head"><h2>{t("pkgScheduledTitle")}</h2></div>
          <div className="pkg-scroll"><table className="tbl"><tbody>{d.scheduled.map((s) => (
            <tr key={s.id}><td>{L(s.plan)}</td><td className="num">{t("pkgFrom", { d: s.startsOn })} → {s.endsOn}</td><td>{t(`pkgChange_${s.changeKind}`)}</td></tr>
          ))}</tbody></table></div>
        </section>
      )}

      <section className="card" data-testid="pkg-request">
        <div className="card-head"><h2>{t("pkgRequestTitle")}</h2><span className="pill warn">{t("pkgManualReview")}</span></div>
        <div className="card-body">
          <p className="pf-small">{t("pkgRequestHint")}</p>
          {d.openRequest ? (
            <div className="pkg-open" data-testid="pkg-open-request">
              <b>{t(`pkgReq_${d.openRequest.kind}`)}{d.openRequest.plan && ` · ${L(d.openRequest.plan)}`}</b>
              <span className="pf-small">{t("pkgRequestOpen", { d: new Date(d.openRequest.createdAt).toLocaleDateString(lang === "ar" ? "ar-SA-u-nu-latn" : "en-GB"), u: d.openRequest.requestedBy })}</span>
              {d.openRequest.note && <span>{d.openRequest.note}</span>}
              {d.canRequest && <div><button className="btn btn-sm" data-testid="pkg-withdraw" onClick={() => void withdraw(d.openRequest!.id)}>{t("pkgWithdraw")}</button></div>}
            </div>
          ) : d.canRequest ? (
            <div className="pkg-form">
              <div className="segmented" role="radiogroup" aria-label={t("pkgRequestKind")}>
                {(["renewal", "change"] as const).map((k) => <label key={k}><input type="radio" name="pkg-kind" checked={form.kind === k} onChange={() => setForm({ ...form, kind: k })} /><span>{t(`pkgReq_${k}`)}</span></label>)}
              </div>
              {form.kind === "change" && (
                <select className="control" aria-label={t("pkgWanted")} data-testid="pkg-wanted" value={form.planId} onChange={(e) => setForm({ ...form, planId: e.target.value })}>
                  <option value="">{t("pkgChoose")}</option>
                  {d.packages.filter((p) => p.kind === "paid").map((p) => <option key={p.id} value={p.id}>{L(p)} · {priceText(t, termsOfPublic(p))}</option>)}
                </select>
              )}
              <textarea className="control" rows={2} maxLength={1000} placeholder={t("pkgNote")} aria-label={t("pkgNote")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              <div><button className="btn btn-primary" data-testid="pkg-send" disabled={busy || (form.kind === "change" && !form.planId)} onClick={() => void send()}><Icon name="refresh" />{t("pkgSend")}</button></div>
            </div>
          ) : <p className="pf-small" data-testid="pkg-owner-only">{t("pkgOwnerOnly")}</p>}
          {d.requests.filter((r) => r.status !== "open").length > 0 && (
            <table className="tbl pkg-reqs"><tbody>{d.requests.filter((r) => r.status !== "open").map((r) => (
              <tr key={r.id}><td>{t(`pkgReq_${r.kind}`)}{r.plan && ` · ${L(r.plan)}`}</td><td><span className={`pill ${r.status === "done" ? "good" : r.status === "declined" ? "bad" : ""}`}>{t(`pkgReqStatus_${r.status}`)}</span></td>
                <td className="pf-small">{r.response ?? ""}</td><td className="num">{r.handledAt ? r.handledAt.slice(0, 10) : ""}</td></tr>
            ))}</tbody></table>
          )}
        </div>
      </section>

      <section className="card list-card" data-testid="pkg-compare-card">
        <div className="card-head"><h2>{t("pkgCompare")}</h2></div>
        <Comparison rows={cmp} current={c?.plan.id} testId="pkg-compare" />
      </section>

      {d.history.length > 0 && (
        <section className="card list-card" data-testid="pkg-history">
          <div className="card-head"><h2>{t("pkgHistory")}</h2></div>
          <div className="pkg-scroll"><table className="tbl"><thead><tr><th>{t("pkgName")}</th><th>{t("pkgKind")}</th><th>{t("pkgDates")}</th><th>{t("pkgHow")}</th><th>{t("pkgPayment")}</th><th /></tr></thead>
            <tbody>{d.history.map(subRow)}</tbody></table></div>
        </section>
      )}
    </Shell>
  );
}
